import React, { useEffect } from 'react';
import styled from 'styled-components';
import Select, { Option } from '@ingka/select';
import Text from '@ingka/text';
import { AvailableStore } from '../services/Appointment/model';
import { layout } from '../styles';
import {
  isStoreRemoteInConsolidatedCalendar,
  ServiceLocation,
} from '../helpers';
import analyticsApiClient from '../services/Analytics';
import { mediaQueries } from '../styles/mediaQueries';
import { useStateDispatch, useStateSelector } from '../store';
import { updateSelectedStore } from '../reducers/app/appReducer';
import { isPlainView } from '../helpers/plainViewHelper';
import useTranslation from '../hooks/useTranslation';
import { getQueryParamsFromURL } from '../helpers/urlHelper';

interface StoreItem {
  text: string;
  value: string;
}

interface StorePickerPropTypes {
  headerText: string;
  stores: AvailableStore[];
  serviceId: string;
  location: ServiceLocation;
  operation?: string;
  isDisabled?: boolean;
}

interface StyledContainerProps {
  $isPlainView: boolean;
}

const Container = styled.div<StyledContainerProps>`
  margin-top: ${layout.contentMarginTop.mobile};
  margin-bottom: 3.5rem;
  ${mediaQueries.portraitTabletUp} {
    display: inline-block;
    margin-top: ${layout.contentMarginTop.desktop};
    width: ${(props: StyledContainerProps) => props.$isPlainView && '100%'};
  }
`;

const StyledHeading = styled(Text)`
  &&& {
    margin-bottom: 2rem;
    margin-top: 2rem;
  }
`;

const StorePicker: React.FunctionComponent<StorePickerPropTypes> = ({
  headerText,
  stores,
  serviceId,
  location,
  operation,
  isDisabled,
}): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useStateDispatch();
  const storeId = useStateSelector((state) => state.app.selected.storeId);

  const { handleEventInGoogleAnalytic } = analyticsApiClient();
  const { defaultStorePostalCode } = CSS_CONFIG.SERVICES.consolidatedCalendar;

  const hideStoreSelector = getQueryParamsFromURL().storeSelector === false;

  const isConsolidatedCalendar = isStoreRemoteInConsolidatedCalendar(
    location,
    serviceId,
    CSS_CONFIG,
  );

  const showConsolidatedCalendar = isConsolidatedCalendar && stores.length > 1;

  const showOnlyOneCalendar =
    isConsolidatedCalendar &&
    CSS_CONFIG.SERVICES.consolidatedCalendar.showOnlyOneCalendar;

  const consolidatedCalendar = {
    value: 'all',
    text: t('pages.timeslots.allStores'),
  };

  const storeList = [
    ...(showConsolidatedCalendar ? [consolidatedCalendar] : []),
    ...stores,
  ];

  const selectedStore =
    storeList.find((store) => store.value === storeId) || storeList[0];

  const onStoreSelect = async (
    event: React.ChangeEvent<HTMLSelectElement>,
  ): Promise<void> => {
    const { value } = event.target;
    dispatch(updateSelectedStore(value));
    handleEventInGoogleAnalytic(
      `switching_store_${serviceId}_${operation}_appointment`,
    );
  };

  useEffect(() => {
    const setInitialSelectedStore = () => {
      const defaultStore =
        showConsolidatedCalendar && defaultStorePostalCode
          ? stores.find(
              (store: AvailableStore) =>
                store.zipCode === defaultStorePostalCode,
            )
          : null;
      const initialStoreId = defaultStore?.value || storeList[0]?.value;
      dispatch(updateSelectedStore(initialStoreId));
    };

    setInitialSelectedStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (showOnlyOneCalendar) {
    return <></>;
  }

  return storeList.length === 1 || hideStoreSelector ? (
    <StyledHeading tagName="p" textStyle="Heading.M">
      {selectedStore.text}
    </StyledHeading>
  ) : (
    <Container $isPlainView={isPlainView()}>
      <StyledHeading tagName="h2" textStyle="Heading.M">
        {headerText}
      </StyledHeading>
      <Select
        onChange={onStoreSelect}
        id="store"
        label={t('components.storePicker.label')}
        hintText=""
        value={storeId}
        disabled={isDisabled}
        data-cy="store-picker"
      >
        {storeList.map((store: StoreItem) => (
          <Option name={store.text} value={store.value} key={store.value} />
        ))}
      </Select>
    </Container>
  );
};

export default StorePicker;
